

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Payment {
  id: string;
  titel: string;
  betrag: number;
  faelligkeitsdatum: string;
  status: 'offen' | 'bezahlt' | 'ueberfaellig';
  kontakt?: string;
  notizen?: string;
  bezahlt_am?: string;
  bezahlt_von?: string;
  zahlungsart?: 'firma' | 'privat';
  datei_url?: string;
  beleg_url?: string;
  erstellt_am: string;
  aktualisiert_am: string;
}

interface Kontakt {
  id: string;
  name: string;
  firma?: string;
}

const zahlungsarten = [
  { value: 'firma', label: 'Firmenkonto' },
  { value: 'privat', label: 'Privates Geld' }
];

export default function Zahlungen() {
  const { user } = useAuth();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [kontakte, setKontakte] = useState<Kontakt[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'offen' | 'bezahlt' | 'alle'>('offen');

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);

  // Selected payment for actions
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  // Form states
  const [addFormData, setAddFormData] = useState({
    titel: '',
    betrag: '',
    faelligkeitsdatum: '',
    kontakt: '',
    notizen: '',
    document: null as File | null
  });

  const [payFormData, setPayFormData] = useState({
    zahlungsart: 'firma' as 'firma' | 'privat',
    bezahlt_von: '',
    bezahlt_am: new Date().toISOString().split('T')[0],
    notizen: '',
    beleg: null as File | null
  });

  // Load data
  useEffect(() => {
    if (user) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadPayments(), loadContacts()]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('zahlungen')
        .select('*')
        .eq('user_id', user.id)
        .order('erstellt_am', { ascending: false });

      if (error) throw error;

      // Update overdue status
      const updatedPayments = data.map(payment => {
        if (payment.status === 'offen' && new Date(payment.faelligkeitsdatum) < new Date()) {
          return { ...payment, status: 'ueberfaellig' };
        }
        return payment;
      });

      setPayments(updatedPayments);
    } catch (error) {
      console.error('Error loading payments:', error);
    }
  };

  const loadContacts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user.id)
        .eq('data_type', 'contact')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const contactData = data.map(item => ({
        id: item.id,
        name: item.data.name,
        firma: item.data.firma
      }));

      setKontakte(contactData);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  // File upload
  const uploadDocument = async (file: File): Promise<{ url: string; error?: any }> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `payment_${Date.now()}.${fileExt}`;
      const filePath = `${user?.id}/payments/${fileName}`;

      const { data, error } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (error) {
        return { url: '', error };
      }

      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      return { url: publicUrl };
    } catch (error) {
      return { url: '', error };
    }
  };

  // Form handlers
  const resetAddForm = () => {
    setAddFormData({
      titel: '',
      betrag: '',
      faelligkeitsdatum: '',
      kontakt: '',
      notizen: '',
      document: null
    });
  };

  const resetPayForm = () => {
    setPayFormData({
      zahlungsart: 'firma',
      bezahlt_von: '',
      bezahlt_am: new Date().toISOString().split('T')[0],
      notizen: '',
      beleg: null
    });
  };

  // Add new payment
  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !addFormData.titel || !addFormData.betrag) return;

    try {
      let documentUrl = null;

      if (addFormData.document) {
        const { url, error } = await uploadDocument(addFormData.document);
        if (error) {
          alert('Fehler beim Hochladen des Dokuments: ' + error.message);
          return;
        }
        documentUrl = url;
      }

      const paymentData = {
        user_id: user.id,
        titel: addFormData.titel,
        betrag: parseFloat(addFormData.betrag),
        faelligkeitsdatum: addFormData.faelligkeitsdatum,
        status: 'offen',
        kontakt: addFormData.kontakt || null,
        notizen: addFormData.notizen || null,
        datei_url: documentUrl,
        erstellt_am: new Date().toISOString(),
        aktualisiert_am: new Date().toISOString()
      };

      const { error } = await supabase
        .from('zahlungen')
        .insert([paymentData]);

      if (error) throw error;

      await loadPayments();
      setIsAddModalOpen(false);
      resetAddForm();
      alert('✅ Zahlung erfolgreich erstellt!');
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('Fehler beim Erstellen der Zahlung.');
    }
  };

  // Mark as paid
  const handleMarkAsPaid = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPayment || !user) return;

    try {
      let belegUrl = null;

      if (payFormData.beleg) {
        const { url, error } = await uploadDocument(payFormData.beleg);
        if (error) {
          alert('Fehler beim Hochladen des Belegs: ' + error.message);
          return;
        }
        belegUrl = url;
      }

      const updateData = {
        status: 'bezahlt',
        bezahlt_am: payFormData.bezahlt_am,
        bezahlt_von: payFormData.bezahlt_von,
        zahlungsart: payFormData.zahlungsart,
        beleg_url: belegUrl,
        notizen: selectedPayment.notizen 
          ? `${selectedPayment.notizen}\n\nZahlungsnotiz: ${payFormData.notizen}`
          : `Zahlungsnotiz: ${payFormData.notizen}`,
        aktualisiert_am: new Date().toISOString()
      };

      const { error } = await supabase
        .from('zahlungen')
        .update(updateData)
        .eq('id', selectedPayment.id)
        .eq('user_id', user.id);

      if (error) throw error;

      await loadPayments();
      setIsPayModalOpen(false);
      setSelectedPayment(null);
      resetPayForm();
      alert('✅ Zahlung als bezahlt markiert!');
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      alert('Fehler beim Markieren der Zahlung.');
    }
  };

  // Delete payment
  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm('Zahlung wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('zahlungen')
        .delete()
        .eq('id', paymentId)
        .eq('user_id', user?.id);

      if (error) throw error;
      await loadPayments();
      alert('✅ Zahlung gelöscht!');
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Fehler beim Löschen der Zahlung.');
    }
  };

  // Filter payments
  const getFilteredPayments = () => {
    switch (activeFilter) {
      case 'offen':
        return payments.filter(p => p.status === 'offen' || p.status === 'ueberfaellig');
      case 'bezahlt':
        return payments.filter(p => p.status === 'bezahlt');
      case 'alle':
        return payments;
      default:
        return payments;
    }
  };

  const filteredPayments = getFilteredPayments();

  // Calculate statistics
  const stats = {
    offene: payments
      .filter(p => p.status === 'offen' || p.status === 'ueberfaellig')
      .reduce((sum, p) => sum + p.betrag, 0),
    
    bezahlte: payments
      .filter(p => p.status === 'bezahlt')
      .reduce((sum, p) => sum + p.betrag, 0),
    
    gesamt: payments
      .filter(p => p.status === 'offen' || p.status === 'ueberfaellig')
      .reduce((sum, p) => sum + p.betrag, 0)
  };

  const formatCurrency = (amount: number) => `CHF ${amount.toFixed(2)}`;

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const kontaktOptionen = [
    { value: '', label: 'Kontakt auswählen...' },
    ...kontakte.map(kontakt => ({
      value: kontakt.name,
      label: kontakt.firma ? `${kontakt.name} (${kontakt.firma})` : kontakt.name
    }))
  ];

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Zahlungen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <i className="ri-bill-line mr-3 text-blue-600"></i>
                Zahlungen verwalten
              </h1>
              <p className="text-gray-600 mt-2">
                Verwalten Sie Rechnungen und Spesen mit Dokumenten
              </p>
            </div>
            <button
              onClick={() => {
                resetAddForm();
                setIsAddModalOpen(true);
              }}
              className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center cursor-pointer whitespace-nowrap shadow-sm transition-colors"
            >
              <i className="ri-add-line mr-2"></i>
              Neue Zahlung
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-red-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-red-600">Offene Rechnungen</p>
                <p className="text-2xl font-bold text-red-700 mt-1">{formatCurrency(stats.offene)}</p>
                <p className="text-xs text-red-500 mt-2">
                  {payments.filter(p => p.status === 'offen' || p.status === 'ueberfaellig').length} Rechnungen
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <i className="ri-file-list-line text-2xl text-red-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-green-600">Bezahlte Rechnungen</p>
                <p className="text-2xl font-bold text-green-700 mt-1">{formatCurrency(stats.bezahlte)}</p>
                <p className="text-xs text-green-500 mt-2">
                  {payments.filter(p => p.status === 'bezahlt').length} Rechnungen
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-600">Gesamt Ausstehend</p>
                <p className="text-2xl font-bold text-purple-700 mt-1">{formatCurrency(stats.gesamt)}</p>
                <p className="text-xs text-purple-500 mt-2">Zu zahlen</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <i className="ri-calculator-line text-2xl text-purple-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl shadow-sm border mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveFilter('offen')}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  activeFilter === 'offen'
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-file-list-line mr-2"></i>
                Offen ({payments.filter(p => p.status === 'offen' || p.status === 'ueberfaellig').length})
              </button>
              <button
                onClick={() => setActiveFilter('bezahlt')}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  activeFilter === 'bezahlt'
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-checkbox-circle-line mr-2"></i>
                Bezahlt ({payments.filter(p => p.status === 'bezahlt').length})
              </button>
              <button
                onClick={() => setActiveFilter('alle')}
                className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                  activeFilter === 'alle'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-list-check-line mr-2"></i>
                Alle ({payments.length})
              </button>
            </div>
          </div>

          {/* Payments List */}
          <div className="p-6">
            {filteredPayments.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <i className="ri-file-list-line text-6xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Keine Zahlungen gefunden</h3>
                <p className="text-gray-500 mb-6">
                  {activeFilter === 'alle' ? 'Erstellen Sie Ihre erste Zahlung.' : `Keine ${activeFilter} Zahlungen vorhanden.`}
                </p>
                <button
                  onClick={() => {
                    resetAddForm();
                    setIsAddModalOpen(true);
                  }}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  <i className="ri-add-line mr-2"></i>
                  Erste Zahlung erstellen
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPayments.map(payment => {
                  const daysUntilDue = getDaysUntilDue(payment.faelligkeitsdatum);
                  const isOverdue = payment.status === 'ueberfaellig';
                  const isDueSoon = payment.status === 'offen' && daysUntilDue >= 0 && daysUntilDue <= 7;

                  // Status-spezifische Farben
                  let cardColor, dotColor, textColor, statusText, statusIcon;

                  if (payment.status === 'bezahlt') {
                    // BEZAHLT
                    cardColor = 'bg-green-50 border-green-200';
                    dotColor = 'bg-green-500';
                    textColor = 'text-green-700';
                    statusText = `Bezahlt am ${payment.bezahlt_am ? new Date(payment.bezahlt_am).toLocaleDateString('de-DE') : 'Unbekannt'}`;
                    statusIcon = 'ri-checkbox-circle-line';
                  } else if (isOverdue) {
                    // ÜBERFÄLLIG
                    cardColor = 'bg-red-50 border-red-200';
                    dotColor = 'bg-red-500';
                    textColor = 'text-red-700';
                    statusText = `Überfällig ${Math.abs(daysUntilDue)} Tage`;
                    statusIcon = 'ri-error-warning-line';
                  } else if (isDueSoon) {
                    // BALD FÄLLIG
                    cardColor = 'bg-yellow-50 border-yellow-200';
                    dotColor = 'bg-yellow-500';
                    textColor = 'text-yellow-700';
                    statusText = daysUntilDue === 0 ? 'Heute fällig' : `Fällig in ${daysUntilDue} Tagen`;
                    statusIcon = 'ri-time-line';
                  } else {
                    // OFFEN
                    cardColor = 'bg-gray-50 border-gray-200';
                    dotColor = 'bg-gray-500';
                    textColor = 'text-gray-700';
                    statusText = 'Offen';
                    statusIcon = 'ri-file-list-line';
                  }

                  return (
                    <div
                      key={payment.id}
                      className={`border rounded-xl p-6 ${cardColor} transition-all hover:shadow-md`}
                    >
                      {/* Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${dotColor} flex-shrink-0`}></div>
                            <div className="min-w-0 flex-1">
                              <h4 className="font-semibold text-gray-900 flex items-center">
                                {payment.titel}
                                {payment.datei_url && (
                                  <i className="ri-attachment-line text-blue-600 ml-2" title="Dokument vorhanden"></i>
                                )}
                                {payment.zahlungsart === 'privat' && (
                                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    Privat
                                  </span>
                                )}
                              </h4>
                              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-600">
                                <span>Fällig: {new Date(payment.faelligkeitsdatum).toLocaleDateString('de-DE')}</span>
                                {payment.kontakt && (
                                  <>
                                    <span className="text-gray-400">•</span>
                                    <span>{payment.kontakt}</span>
                                  </>
                                )}
                                {payment.bezahlt_von && (
                                  <>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-green-600">Von: {payment.bezahlt_von}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Betrag und Status */}
                        <div className="flex items-center justify-between sm:justify-end space-x-4">
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">
                              {formatCurrency(payment.betrag)}
                            </p>
                            <div className={`flex items-center justify-end mt-1 ${textColor}`}>
                              <i className={`${statusIcon} mr-1 text-sm`}></i>
                              <span className="text-sm font-medium">{statusText}</span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex flex-col gap-2">
                            {/* Als bezahlt markieren */}
                            {(payment.status === 'offen' || payment.status === 'ueberfaellig') && (
                              <button
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  resetPayForm();
                                  setIsPayModalOpen(true);
                                }}
                                className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 cursor-pointer transition-colors whitespace-nowrap"
                              >
                                <i className="ri-check-line mr-1"></i>
                                Bezahlt
                              </button>
                            )}

                            {/* Edit/Delete */}
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleDeletePayment(payment.id)}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg cursor-pointer transition-colors"
                                title="Löschen"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Notizen */}
                      {payment.notizen && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-start space-x-2">
                            <i className="ri-sticky-note-line text-gray-400 mt-0.5"></i>
                            <p className="text-sm text-gray-600">{payment.notizen}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Payment Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          resetAddForm();
        }}
        title="Neue Zahlung hinzufügen"
        maxWidth="lg"
      >
        <form onSubmit={handleAddPayment} className="space-y-6">
          <Input
            label="Titel/Beschreibung"
            value={addFormData.titel}
            onChange={e => setAddFormData(prev => ({ ...prev, titel: e.target.value }))}
            placeholder="z.B. Miete Büro, Rechnung Lieferant..."
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Betrag (CHF)"
              type="number"
              step="0.01"
              min="0"
              value={addFormData.betrag}
              onChange={e => setAddFormData(prev => ({ ...prev, betrag: e.target.value }))}
              placeholder="0.00"
              required
            />

            <Input
              label="Fälligkeitsdatum"
              type="date"
              value={addFormData.faelligkeitsdatum}
              onChange={e => setAddFormData(prev => ({ ...prev, faelligkeitsdatum: e.target.value }))}
              required
            />
          </div>

          <Select
            label="Kontakt/Anbieter (optional)"
            options={kontaktOptionen}
            value={addFormData.kontakt}
            onChange={value => setAddFormData(prev => ({ ...prev, kontakt: value as string }))}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen (optional)</label>
            <textarea
              value={addFormData.notizen}
              onChange={e => setAddFormData(prev => ({ ...prev, notizen: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              rows={3}
              placeholder="Zusätzliche Informationen..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="ri-attachment-line mr-1"></i> Dokument anhängen (optional)
            </label>
            <input
              type="file"
              onChange={e => setAddFormData(prev => ({ ...prev, document: e.target.files?.[0] || null }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
            />
            <p className="mt-2 text-xs text-gray-500">
              PDF, Word, Excel oder Bild-Dateien (max. 10MB)
            </p>
          </div>

          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
            <Button type="submit" className="w-full sm:flex-1">
              <i className="ri-save-line mr-2"></i>
              Zahlung erstellen
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:flex-1"
              onClick={() => {
                setIsAddModalOpen(false);
                resetAddForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Mark as Paid Modal */}
      <Modal
        isOpen={isPayModalOpen}
        onClose={() => {
          setIsPayModalOpen(false);
          setSelectedPayment(null);
          resetPayForm();
        }}
        title="Zahlung als bezahlt markieren"
        maxWidth="lg"
      >
        {selectedPayment && (
          <form onSubmit={handleMarkAsPaid} className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">{selectedPayment.titel}</h4>
              <p className="text-sm text-blue-700">
                Betrag: <strong>{formatCurrency(selectedPayment.betrag)}</strong>
              </p>
              <p className="text-sm text-blue-700">
                Fällig: {new Date(selectedPayment.faelligkeitsdatum).toLocaleDateString('de-DE')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Zahlungsart"
                options={zahlungsarten}
                value={payFormData.zahlungsart}
                onChange={value => setPayFormData(prev => ({ ...prev, zahlungsart: value as 'firma' | 'privat' }))}
                required
              />

              <Input
                label="Bezahldatum"
                type="date"
                value={payFormData.bezahlt_am}
                onChange={e => setPayFormData(prev => ({ ...prev, bezahlt_am: e.target.value }))}
                required
              />
            </div>

            <Input
              label="Bezahlt von (Name)"
              value={payFormData.bezahlt_von}
              onChange={e => setPayFormData(prev => ({ ...prev, bezahlt_von: e.target.value }))}
              placeholder="z.B. Max Mustermann"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Zahlungsnotizen (optional)</label>
              <textarea
                value={payFormData.notizen}
                onChange={e => setPayFormData(prev => ({ ...prev, notizen: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows={3}
                placeholder="Zusätzliche Informationen zur Zahlung..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <i className="ri-receipt-line mr-1"></i> Zahlungsbeleg anhängen (optional)
              </label>
              <input
                type="file"
                onChange={e => setPayFormData(prev => ({ ...prev, beleg: e.target.files?.[0] || null }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
              />
            </div>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
              <Button type="submit" className="w-full sm:flex-1">
                <i className="ri-check-line mr-2"></i>
                Als bezahlt markieren
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:flex-1"
                onClick={() => {
                  setIsPayModalOpen(false);
                  setSelectedPayment(null);
                  resetPayForm();
                }}
              >
                Abbrechen
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}

